import type {
  DocumentTemplateSummary,
  GeneratedDocumentDetail,
  GeneratedDocumentEditorDetail,
  GeneratedDocumentLogEntry,
  UpdateGeneratedDocumentPayload,
} from "@/types/generated-document";

import { requestData } from "@/api/api-config";

const basePath = "user/patient";

async function listGeneratedDocuments(
  patientId: string,
): Promise<GeneratedDocumentDetail[] | null> {
  return await requestData<GeneratedDocumentDetail[]>(
    `${basePath}/${patientId}/generated-documents`,
    "get",
    undefined,
    false,
  );
}

async function listTemplates(): Promise<DocumentTemplateSummary[] | null> {
  return await requestData<DocumentTemplateSummary[]>(
    `${basePath}/document-templates`,
    "get",
    undefined,
    false,
  );
}

async function getGeneratedDocument(
  id: string,
): Promise<GeneratedDocumentEditorDetail | null> {
  return await requestData<GeneratedDocumentEditorDetail>(
    `${basePath}/generated-documents/${id}`,
    "get",
    undefined,
    false,
  );
}

async function generateDocument(
  noteId: string,
  templateId?: string,
): Promise<GeneratedDocumentEditorDetail | null> {
  return await requestData<GeneratedDocumentEditorDetail>(
    `${basePath}/generated-documents`,
    "post",
    { json: { noteId, templateId } },
    false,
  );
}

async function updateGeneratedDocument(
  id: string,
  data: UpdateGeneratedDocumentPayload,
): Promise<GeneratedDocumentEditorDetail | null> {
  return await requestData<GeneratedDocumentEditorDetail>(
    `${basePath}/generated-documents/${id}`,
    "put",
    { json: data },
    false,
  );
}

async function sendGeneratedDocumentEmail(
  id: string,
  data?: { subject?: string; body?: string },
): Promise<GeneratedDocumentEditorDetail | null> {
  return await requestData<GeneratedDocumentEditorDetail>(
    `${basePath}/generated-documents/${id}/send-email`,
    "post",
    { json: data ?? {} },
    false,
  );
}

async function shareGeneratedDocument(
  id: string,
  data?: { patientMessage?: string },
): Promise<GeneratedDocumentEditorDetail | null> {
  return await requestData<GeneratedDocumentEditorDetail>(
    `${basePath}/generated-documents/${id}/share`,
    "patch",
    { json: data ?? {} },
    false,
  );
}

async function archiveGeneratedDocument(id: string): Promise<null> {
  return await requestData(
    `${basePath}/generated-documents/${id}`,
    "delete",
    undefined,
    false,
  );
}

async function getGeneratedDocumentLogs(
  id: string,
): Promise<GeneratedDocumentLogEntry[] | null> {
  return await requestData<GeneratedDocumentLogEntry[]>(
    `${basePath}/generated-documents/${id}/logs`,
    "get",
    undefined,
    false,
  );
}

export const generatedDocumentApi = {
  list: listGeneratedDocuments,
  get: getGeneratedDocument,
  generate: generateDocument,
  update: updateGeneratedDocument,
  sendEmail: sendGeneratedDocumentEmail,
  share: shareGeneratedDocument,
  archive: archiveGeneratedDocument,
  getLogs: getGeneratedDocumentLogs,
  listTemplates,
};
